import React, { useCallback, useEffect, useRef, useState } from 'react';
import './Info.css';
import { Link } from 'react-router-dom';
import Cite from '../utils/Cite';

const HOVER_COLORS = ['#ffc964', '#d65952', '#7ec28a', '#6ba1cf', '#a089f1'];
const randomColor = () => HOVER_COLORS[Math.floor(Math.random() * HOVER_COLORS.length)];

// ?from=info tells the shop we've already had a user gesture (the click
// that opened the overlay), so it should skip the ENTER gate and land
// straight on the exterior with audio ready to go.
const SHOP_URL = '/shop/?from=info';

// Each link is either internal (`to`) or external (`href`). External entries
// can opt into a hover-ambient — bodega street audio fades in while the
// cursor is over the link, fades out on mouseleave.
const INFO_LINKS = [
  { to: '/', label: 'Work' },
  // The CV is a PDF in /cv/, exported from the CV doc. `newTab` opens it in
  // a new tab so the site stays where it was.
  { href: '/cv/Luke-Crawford-CV.pdf', label: 'CV', newTab: true },
  // The shop is not open. Left in place and struck through rather than deleted:
  // it says the shop exists and isn't taking orders, which a missing line
  // doesn't. `closed` renders it as plain text, so there's nothing to click.
  { label: 'Shop', closed: true },
  { to: '/contact', label: 'Contact' },
];

export default function Info() {
  const glitch = useCallback(() => {
    const container = document.querySelector('.container');
    if (!container) return;
    container.classList.remove('glitch-shake');
    void container.offsetWidth; // force reflow to restart animation
    container.classList.add('glitch-shake');
  }, []);

  // Hover-ambient audio for external links that opt in (Shop)
  const ambientRef = useRef(null);
  const fadeRef = useRef(null);

  const ensureAmbient = (src) => {
    if (!ambientRef.current) {
      const a = new Audio(src);
      a.loop = true;
      a.volume = 0;
      ambientRef.current = a;
    }
    return ambientRef.current;
  };

  const fadeTo = (target, duration = 600) => {
    const a = ambientRef.current;
    if (!a) return;
    if (fadeRef.current) clearInterval(fadeRef.current);
    const start = a.volume;
    const t0 = Date.now();
    fadeRef.current = setInterval(() => {
      const t = Math.min((Date.now() - t0) / duration, 1);
      a.volume = start + (target - start) * t;
      if (t >= 1) {
        clearInterval(fadeRef.current);
        fadeRef.current = null;
        if (target === 0) a.pause();
      }
    }, 30);
  };

  const onAmbientEnter = (src) => {
    const a = ensureAmbient(src);
    a.play().catch(() => {});
    fadeTo(0.5);
  };
  const onAmbientLeave = () => fadeTo(0);

  // Stop audio if the page unmounts (user navigates away mid-fade)
  useEffect(() => () => {
    if (fadeRef.current) clearInterval(fadeRef.current);
    if (ambientRef.current) {
      ambientRef.current.pause();
      ambientRef.current = null;
    }
  }, []);

  // Shop iframe overlay — opens fullscreen when user clicks the Shop link
  // (instead of navigating away). Keeps the site mounted and the audio
  // context alive, so the hover ambient flows seamlessly into the shop's
  // own ambient. Escape key closes it.
  const [shopOpen, setShopOpen] = useState(false);
  const [shopScene, setShopScene] = useState('exterior'); // tracks shop's current scene via postMessage
  const closeShop = useCallback(() => {
    setShopOpen(false);
    setShopScene('exterior'); // reset for next open
  }, []);
  const openShopOverlay = useCallback(() => {
    fadeTo(0); // fade out the site's ambient — shop's iframe takes over
    setShopOpen(true);
  }, []);
  useEffect(() => {
    if (!shopOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') closeShop(); };
    const onMessage = (e) => {
      if (e.data?.type === 'shop:scene' && typeof e.data.scene === 'string') {
        setShopScene(e.data.scene);
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('message', onMessage);
    };
  }, [shopOpen, closeShop]);

  return (
    <div>
      <div className="home-container small-width">
        <div className="container">
          <div className="body-text">
            I make things. Concept, copy, art direction, and sometimes the whole thing from scratch. I'm a senior creative with 14 years in agencies in Edinburgh, London and New York, writing and art directing for brands like Nike, DAZN and Honda. I make my own work too: creative direction and identity for artists, generative art, and whatever else I can't stop thinking about. Based in Edinburgh. Open to roles, collaborations and good problems.
          </div>

          <ul className="info-links">
            {INFO_LINKS.map((link) => {
              const color = { '--hover-color': randomColor() };
              if (link.href) {
                const handlers = link.ambient ? {
                  onMouseEnter: () => onAmbientEnter(link.ambient),
                  onMouseLeave: onAmbientLeave,
                } : {};
                const onClick = link.overlay ? (e) => {
                  // Only intercept plain left-click — let cmd/ctrl/middle-click
                  // open in a new tab as normal.
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  openShopOverlay();
                } : undefined;
                return (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      className="title-link-secondary"
                      style={color}
                      onClick={onClick}
                      {...(link.newTab ? { target: '_blank', rel: 'noreferrer' } : {})}
                      {...handlers}
                    >
                      <span className="title-text-secondary">{link.label}</span>
                    </a>
                  </li>
                );
              }
              if (link.closed) {
                return (
                  <li key={link.label}>
                    <span className="info-link-closed title-text-secondary">{link.label}</span>
                  </li>
                );
              }
              return (
                <li key={link.to}>
                  <Link to={link.to} className="title-link-secondary" style={color}>
                    <span className="title-text-secondary">{link.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {shopOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999, background: '#000',
          }}
        >
          <iframe
            src={SHOP_URL}
            allow="autoplay"
            title="Thingdoms Shop"
            style={{ display: 'block', width: '100vw', height: '100vh', border: 0 }}
          />
          {/* Only show the close button on the exterior — once inside the shop,
              the shop's own "← OUT" button handles in-shop navigation. Closing
              requires going back to exterior first, then clicking the X.
              Escape still closes from anywhere. The X sits at low opacity by
              default so it doesn't compete with the bodega visuals; full
              opacity on hover. */}
          {shopScene === 'exterior' && (
            <button
              onClick={closeShop}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.18'; }}
              aria-label="Exit shop"
              style={{
                position: 'fixed', top: 16, left: 16, zIndex: 10000,
                width: 32, height: 32,
                background: 'rgba(0,0,0,0.5)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.4)',
                padding: 0,
                fontSize: 14,
                lineHeight: 1,
                cursor: 'pointer',
                opacity: 0.18,
                transition: 'opacity 280ms ease',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              ✕
            </button>
          )}
        </div>
      )}
    </div>
  );
};
